import { useState, useEffect, useCallback } from 'react';
import { Wrench, Play, Check, RefreshCw, AlertTriangle, Zap, Clock, CheckCircle } from 'lucide-react';
import { SeverityBadge } from './SEOOSShared';
import { Button } from '@/components/ui/button';

const API = '';

export default function SEOOSFixCenter() {
  const [fixes, setFixes] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState({});
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/seoos/fixes`, { headers }).then(r => r.json()),
      fetch(`${API}/api/seoos/fixes/log`, { headers }).then(r => r.json()),
    ]).then(([f, l]) => { setFixes(f.fixes || []); setLog(l.log || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const executeFix = async (fixId) => {
    setExecuting(prev => ({ ...prev, [fixId]: true }));
    try {
      const res = await fetch(`${API}/api/seoos/fixes/${fixId}/execute`, { method: 'POST', headers });
      const data = await res.json();
      console.log(`Fix ${fixId} result:`, data);
      if (data.error) {
        alert(`Hata: ${data.error}`);
      }
    } catch (err) {
      console.error(`Fix ${fixId} error:`, err);
      alert(`Fix calistirilirken hata: ${err.message}`);
    }
    await load();
    setExecuting(prev => ({ ...prev, [fixId]: false }));
  };

  const executeAll = async (priority) => {
    const pFixes = fixes.filter(f => f.priority === priority && f.ready);
    for (const f of pFixes) {
      setExecuting(prev => ({ ...prev, [f.id]: true }));
      try {
        await fetch(`${API}/api/seoos/fixes/${f.id}/execute`, { method: 'POST', headers });
      } catch (err) {
        console.error(`Fix ${f.id} error:`, err);
      }
      setExecuting(prev => ({ ...prev, [f.id]: false }));
    }
    await load();
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>;

  const p0 = fixes.filter(f => f.priority === 'P0');
  const p1 = fixes.filter(f => f.priority === 'P1');

  return (
    <div className="space-y-6" data-testid="seoos-fix-center">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
          <Wrench className="w-5 h-5 inline mr-2 text-purple-400" />AutoFix Merkezi
        </h2>
        <Button onClick={() => executeAll('P0')} className="bg-red-600 hover:bg-red-500 text-white text-xs">
          <Zap className="w-3.5 h-3.5 mr-1" />Tum P0 Fixleri Calistir
        </Button>
      </div>

      {/* P0 Fixes */}
      <div>
        <h3 className="text-sm font-bold text-red-400 uppercase mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />Acil Duzeltmeler (P0)
        </h3>
        <div className="space-y-2">
          {p0.map(fix => (
            <div key={fix.id} className={`flex items-center gap-4 px-5 py-4 rounded-xl border ${fix.ready ? 'bg-red-500/5 border-red-500/20' : 'bg-slate-900/30 border-slate-800/30'}`}>
              <span className="text-xs font-bold text-red-400 bg-red-500/10 px-2 py-0.5 rounded border border-red-500/30">{fix.priority}</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-white">{fix.title}</p>
                <p className="text-[10px] text-slate-500">{fix.affected} sayfa etkilenecek | {fix.impact}</p>
              </div>
              <Button onClick={() => executeFix(fix.id)} disabled={!fix.ready || executing[fix.id]} size="sm"
                className={`text-xs h-8 ${fix.ready ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-slate-800 text-slate-600'}`}>
                {executing[fix.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                {executing[fix.id] ? 'Calisiyor...' : fix.ready ? 'Calistir' : 'Tamamlandi'}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* P1 Fixes */}
      <div>
        <h3 className="text-sm font-bold text-orange-400 uppercase mb-3">Yuksek Oncelik (P1)</h3>
        <div className="space-y-2">
          {p1.map(fix => (
            <div key={fix.id} className="flex items-center gap-4 px-5 py-4 rounded-xl border bg-slate-900/30 border-slate-800/50">
              <span className="text-xs font-bold text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded border border-orange-500/30">{fix.priority}</span>
              <div className="flex-1">
                <p className="text-sm font-bold text-white">{fix.title}</p>
                <p className="text-[10px] text-slate-500">{fix.affected} sayfa | {fix.impact}</p>
              </div>
              <Button onClick={() => executeFix(fix.id)} disabled={!fix.ready || executing[fix.id]} size="sm" className="bg-orange-600 hover:bg-orange-500 text-white text-xs h-8">
                {executing[fix.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                {executing[fix.id] ? 'Calisiyor...' : 'Calistir'}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Execution Log */}
      {log.length > 0 && (
        <div className="bg-slate-900/40 border border-slate-800/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-800/30"><h3 className="text-sm font-bold text-white uppercase">Calisma Gecmisi</h3></div>
          <div className="divide-y divide-slate-800/20">
            {log.slice(0, 10).map((entry, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3">
                {entry.status === 'completed' ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
                <div className="flex-1">
                  <p className="text-xs font-medium text-slate-300">{entry.fix_type} - {entry.detail}</p>
                  <p className="text-[10px] text-slate-600">{entry.started_at?.slice(0, 19)}</p>
                </div>
                <span className="text-xs text-emerald-400">{entry.affected} degisiklik</span>
                <span className="text-[10px] text-slate-500">{entry.seo_gain}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
